import React, { useEffect, useRef, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/Components/ui/form'
import { Input } from '@/Components/ui/input'
import { Button } from '@/Components/ui/button'
import { useLoaderData, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import BodyCard from '@/Components/BodyCard'
import HeaderCard from '@/Components/HeaderCard'
import { Label } from '@/Components/ui/label'
import WMSSettingsService from '@/Service/WMSSettingsService'
import { Textarea } from '@/Components/ui/textarea'
import UserService from '@/Service/UserService'
import { Eye, EyeClosed } from 'lucide-react'
import { Checkbox } from '@/Components/ui/checkbox'
import { checkPermission } from '@/utils/helper'
import RequiredPermission from '@/assets/data/RequiredPermission'
import { useAuth } from '@/Context/AuthContext'
import Loader from '@/Components/Common/Loader'
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/Components/ui/select'

const UpdateUser = () => {
    const { userInfo } = useAuth()
    const formRef = useRef(null)
    const { email } = useParams()
    const { userData, roleData } = useLoaderData()
    const navigate = useNavigate()
    const [roles, setRoles] = useState(roleData)
    const [showPassword, setShowPassword] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const formSchema = z.object({
        email: z
            .string({
                message: 'Email is required',
            })
            .min(1, {
                message: 'Email is required',
            }),
        username: z
            .string({
                message: 'Username is required',
            })
            .min(1, {
                message: 'Username is required',
            }),
        password: z.string().optional(),
        first_name: z
            .string({
                message: 'First Name is required',
            })
            .min(1, {
                message: 'First Name is required',
            }),
        last_name: z
            .string({
                message: 'Last Name is required',
            })
            .min(1, {
                message: 'Last Name is required',
            }),
        role: z
            .array(
                z.string().min(1, {
                    message: 'Role is required',
                })
            )
            .nonempty({ message: 'Role is required' }),
        enabled: z.string(),
        middle_name: z.string(1, { message: 'Role is required' }).optional().nullable(),
        // restrict_ip: z.string().optional(),
    })

    const form = useForm({
        shouldFocusError: false,
        resolver: zodResolver(formSchema),
        defaultValues: {
            email: '',
            username: '',
            first_name: '',
            last_name: '',
            password: '',
            role: [],
            middle_name: '',
            enabled: '',
        },
    })

    async function onSubmit(values) {
        try {
            setIsSaving(true)
            delete userInfo.role
            if (values.password === '') delete values.password
            const resp = await UserService.updateUser(values)
            if (resp.data.success) {
                setIsSaving(false)
            }
        } catch (err) {
            console.log('error', err)
            setIsSaving(false)
        }
    }

    const getRoles = async () => {
        try {
            const resp = await WMSSettingsService.getRoles()
            if (resp.data.success && resp.data.data.length) {
                setRoles(resp.data.data)
            }
        } catch (err) {}
    }

    useEffect(() => {
        getRoles()
        getUser()
    }, [email])

    const getUser = async () => {
        try {
            if (userData) {
                form.reset({ ...userData, role: userData.roles.map((role) => role.role), password: '' })
            }
        } catch (err) {
            navigate('/user')
        }
    }

    function onError(errors, e) {
        const errorKeys = Object.keys(errors)
        if (errorKeys.length > 0 && errors[errorKeys[0]]?.message) {
            toast.error(errors[errorKeys[0]].message)
        } else {
            toast.error('Something went wrong.')
        }
    }

    const handleSaveClick = () => {
        if (formRef.current) {
            formRef.current.requestSubmit()
        }
    }
    return (
        <>
            <HeaderCard>
                <div className="flex justify-end items-center">
                    <div className="flex items-center justify-end gap-2">
                        <Button
                            className="bg-primary-purpal hover:bg-primary-purpal px-4"
                            onClick={handleSaveClick}
                            iconType="Save"
                            disabled={isSaving}
                        >
                            Save {isSaving && <Loader fullScreen={false} />}
                        </Button>
                    </div>
                </div>
            </HeaderCard>
            <BodyCard>
                <Form {...form}>
                    <form ref={formRef} onSubmit={form.handleSubmit(onSubmit, onError)}>
                        <div className="grid grid-cols-2 gap-y-3 gap-x-6">
                            <FormField
                                control={form.control}
                                name="email"
                                render={({ field }) => (
                                    <FormItem className="space-y-1">
                                        <FormLabel required={true}>Email</FormLabel>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                className="shadow-none focus-visible:ring-transparent space-0 mt-0"
                                                placeholder="Email"
                                                disabled={true}
                                                onChange={(e) => {
                                                    field.onChange(e.target.value)
                                                }}
                                            />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="enabled"
                                render={({ field }) => (
                                    <FormItem className="space-y-1">
                                        <FormLabel required={true}>Status</FormLabel>
                                        <FormControl>
                                            <Select {...field} name="enabled" onValueChange={(val) => field.onChange(val)}>
                                                <SelectTrigger className="">
                                                    <SelectValue placeholder="Select a Status" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectGroup>
                                                        <SelectItem value="1">Active</SelectItem>
                                                        <SelectItem value="0">Inactive</SelectItem>
                                                    </SelectGroup>
                                                </SelectContent>
                                            </Select>
                                        </FormControl>
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="username"
                                render={({ field }) => (
                                    <FormItem className="space-y-1">
                                        <FormLabel className="hidden">Username</FormLabel>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                className="shadow-none focus-visible:ring-transparent space-0 mt-0 hidden"
                                                placeholder="Username"
                                                disabled={true}
                                            />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                        </div>
                        <div className="grid mt-3 grid-cols-2 gap-y-3 gap-x-6">
                            <div className="grid grid-cols-1 gap-y-3 gap-x-6">
                                <FormField
                                    control={form.control}
                                    name="first_name"
                                    render={({ field }) => (
                                        <FormItem className="space-y-1">
                                            <FormLabel required={true}>First Name</FormLabel>
                                            <FormControl>
                                                <Input
                                                    className="shadow-none focus-visible:ring-transparent space-0 mt-0"
                                                    placeholder="First Name"
                                                    {...field}
                                                    onChange={(e) => {
                                                        field.onChange(e.target.value)
                                                        let username = e.target.value + ' ' + form.getValues('last_name')
                                                        form.setValue('username', username.trim())
                                                    }}
                                                />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="middle_name"
                                    render={({ field }) => (
                                        <FormItem className="space-y-1">
                                            <FormLabel>Middle Name</FormLabel>
                                            <FormControl>
                                                <Input
                                                    className="shadow-none focus-visible:ring-transparent space-0 mt-0"
                                                    placeholder="Middle Name"
                                                    {...field}
                                                />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="last_name"
                                    render={({ field }) => (
                                        <FormItem className="space-y-1">
                                            <FormLabel required={true}>Last Name</FormLabel>
                                            <FormControl>
                                                <Input
                                                    className="shadow-none focus-visible:ring-transparent space-0 mt-0"
                                                    placeholder="Last Name"
                                                    {...field}
                                                    onChange={(e) => {
                                                        field.onChange(e.target.value)
                                                        let username = form.getValues('first_name') + ' ' + e.target.value
                                                        form.setValue('username', username.trim())
                                                    }}
                                                />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                            </div>
                            <div className="grid grid-cols-1 gap-y-3 gap-x-6">
                                <FormField
                                    control={form.control}
                                    name="password"
                                    render={({ field }) => (
                                        <FormItem className="space-y-1">
                                            <FormLabel>Password</FormLabel>
                                            <div className="relative">
                                                <FormControl>
                                                    <Input
                                                        {...field}
                                                        type={showPassword ? 'text' : 'password'}
                                                        className=" focus-visible:ring-transparent space-0 mt-0 text-security"
                                                        placeholder="***************"
                                                        autoComplete="new-password"
                                                        name="password"
                                                    />
                                                </FormControl>
                                                <div className=" absolute top-2 right-2" onClick={() => setShowPassword(!showPassword)}>
                                                    {showPassword ? <EyeClosed className="text-gray-400" /> : <Eye className="text-gray-400" />}
                                                </div>
                                            </div>
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </div>
                        {checkPermission(RequiredPermission.USER.CHECK_ROLE, userInfo.roles) && (
                            <div className="grid mt-5 grid-cols-1 gap-y-3 gap-x-6">
                                <FormField
                                    control={form.control}
                                    name="role"
                                    render={() => (
                                        <FormItem className="space-y-1 flex flex-col justify-around">
                                            <FormLabel required={true} className="mb-3">
                                                User Role
                                            </FormLabel>

                                            <div className="grid grid-cols-4 gap-y-3 gap-x-6">
                                                {roles.map((item) => (
                                                    <FormField
                                                        key={item.role}
                                                        control={form.control}
                                                        name="role"
                                                        render={({ field }) => {
                                                            return (
                                                                <FormItem key={item.role} className="flex flex-row items-start space-x-3 space-y-0">
                                                                    <FormControl>
                                                                        <Checkbox
                                                                            checked={field.value?.includes(item.roles)}
                                                                            onCheckedChange={(checked) => {
                                                                                return checked
                                                                                    ? field.onChange([...field.value, item.roles])
                                                                                    : field.onChange(
                                                                                          field.value?.filter((value) => value !== item.roles)
                                                                                      )
                                                                            }}
                                                                        />
                                                                    </FormControl>
                                                                    <span className="text-sm  font-light">{item.roles}</span>
                                                                </FormItem>
                                                            )
                                                        }}
                                                    />
                                                ))}
                                            </div>
                                        </FormItem>
                                    )}
                                />
                            </div>
                        )}
                    </form>
                </Form>
            </BodyCard>
        </>
    )
}

export default UpdateUser
